import React from 'react';
import { View, ScrollView, StyleSheet } from 'react-native';
import moment from 'moment';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import Typography from '../../../../components/shared/Typography/Typography.component';
import ExtraInfo from '../../../../components/shared/ExtraInfo/ExtraInfo.component';
import colors from '../../../../config/colors';
import { i18nService } from '../../../../services/i18n.service';
import { InvitationHistoryEntry } from './InviteHistoryDrawer.api';

const ICON_SIZE = 20;

/** the timestamps are ISO-8601, so a plain string compare orders them by date */
const newestFirst = (entries: InvitationHistoryEntry[]) =>
	[...entries].sort((a, b) => b.timestamp.localeCompare(a.timestamp));

const entryLabel = (entry: InvitationHistoryEntry) =>
	entry.channel === 'EMAIL'
		? i18nService.translate(
				'usersManagement.inviteHistory.sentEmail',
				undefined,
				{ to: entry.email },
		  )
		: i18nService.translate(
				'usersManagement.inviteHistory.sentSms',
				undefined,
				{ to: entry.phone },
		  );

type InviteHistoryListProps = {
	entries: InvitationHistoryEntry[];
};

/** Timeline of every invitation sent to the user, newest first. */
const InviteHistoryList = ({ entries }: InviteHistoryListProps) => {
	const sorted = newestFirst(entries);

	return (
		<ScrollView style={styles.scroll}>
			<View style={styles.scrollContent}>
				{sorted.map((entry, index) => {
					const isLast = index === sorted.length - 1;

					return (
						<View key={`${entry.timestamp}-${index}`} style={styles.entry}>
							<View style={styles.texts}>
								<Typography size={14} color='clockRecordBlue'>
									{entryLabel(entry)}
								</Typography>
								<Typography
									size={12}
									color='greyText'
									style={styles.timestamp}
								>
									{moment(entry.timestamp).format('HH:mm ,DD/MM/YYYY')}
								</Typography>
							</View>

							<View style={styles.iconColumn}>
								{entry.status === 'SUCCESS' ? (
									<ExtraInfo
										iconName='circleCheck'
										iconSize={ICON_SIZE}
										iconStyle={{ marginRight: 0, marginLeft: 0 }}
										variant='card'
										placement='top'
										infoText={i18nService.translate(
											'usersManagement.inviteHistory.sendSucceededTooltip',
											undefined,
										)}
									/>
								) : (
									<ExtraInfo
										iconName='alertCircle'
										iconSize={ICON_SIZE}
										iconColor={colors.red}
										iconStyle={{ marginRight: 0, marginLeft: 0 }}
										variant='card'
										placement='top'
										infoText={i18nService.translate(
											'usersManagement.inviteHistory.sendFailedTooltip',
											undefined,
											{ reason: entry.failureReason },
										)}
									/>
								)}
								{/* one connector per entry, so it scrolls along with the list */}
								{!isLast && <View style={styles.connector} />}
							</View>
						</View>
					);
				})}
			</View>
		</ScrollView>
	);
};

const styles = StyleSheet.create({
	// minHeight lets the list shrink inside the drawer instead of pushing it open
	// writingDirection 'rtl' -> 'ltr' is for the scrollbar to stick in left
	scroll: {
		flex: 1,
		minHeight: 0,
		writingDirection: 'rtl',
	},
	scrollContent: {
		writingDirection: 'ltr',
	},
	entry: {
		flexDirection: 'row',
		alignItems: 'stretch',
	},
	// the padding is the gap to the next entry - the connector grows into it
	texts: {
		flex: 1,
		paddingBottom: 20,
	},
	timestamp: {
		marginTop: 2,
	},
	iconColumn: {
		width: ICON_SIZE,
		alignItems: 'center',
		marginLeft: 12,
	},
	connector: {
		width: 1,
		//flex: 1,
		height: 22,
		marginTop: 4,
		backgroundColor: colors.greySeparator,
	},
});

export default InviteHistoryList;
